/**
 * AgentShowcase
 * -------------
 * How each analysis presents itself on the free preview.
 *
 * The previous version listed what an agent measures, which reads like a spec
 * sheet. This shows the *shape* of the report they would receive — the metric
 * rows, laid out exactly as they will appear — with the values masked. Seeing
 * the frame and not the number is what creates the pull; a bullet list does
 * not.
 *
 * Deliberately masked, never invented: showing a plausible-looking symmetry
 * figure before any analysis has run would be a fabricated medical value. The
 * dots are visibly placeholders.
 *
 * Visual language follows the brand book — Midnight Navy field, Candlelight as
 * the light in it, brass hairlines and eyebrows, and no colour outside the
 * palette.
 */
import { Box, Typography, Stack } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { localized } from '@/utils/localizedContent';

/**
 * The signature mark for an agent, chosen by WHAT IT READS, not by position in
 * the list. Keying it to the index meant a filler model could be given the
 * facial-thirds mark, which is arbitrary and undermines the card.
 */
function Emblem({ agentKey }) {
  const common = {
    fill: 'none',
    stroke: '#C0A786',
    strokeWidth: 1.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  const head = <ellipse cx="24" cy="24" rx="14" ry="18" strokeOpacity=".45" />;

  const marks = {
    // Botox — muscles travelling between expressions
    tern: (
      <g {...common}>
        {head}
        <path d="M16 20l-5-5M32 20l5-5M18 33l-4 5M30 33l4 5" strokeOpacity=".95" />
        <circle cx="16" cy="20" r="1.8" fill="#C0A786" stroke="none" />
        <circle cx="32" cy="20" r="1.8" fill="#C0A786" stroke="none" />
        <circle cx="18" cy="33" r="1.8" fill="#C0A786" stroke="none" />
        <circle cx="30" cy="33" r="1.8" fill="#C0A786" stroke="none" />
      </g>
    ),
    // Filler — light pooling over volume
    serval: (
      <g {...common}>
        {head}
        <ellipse cx="17" cy="24" rx="5" ry="4" strokeOpacity=".9" />
        <ellipse cx="31" cy="24" rx="5" ry="4" strokeOpacity=".9" />
        <ellipse cx="17" cy="24" rx="2.2" ry="1.8" strokeOpacity=".55" />
        <ellipse cx="31" cy="24" rx="2.2" ry="1.8" strokeOpacity=".55" />
        <path d="M19 35c3 2.5 7 2.5 10 0" strokeOpacity=".8" />
      </g>
    ),
    // Lift — the jawline and the angle beneath it
    lynx: (
      <g {...common}>
        {head}
        <path d="M11 22c1 11 7 18 13 20 6-2 12-9 13-20" strokeOpacity=".95" />
        <path d="M24 42v4M24 46h9" strokeOpacity=".7" />
      </g>
    ),
    // Rhinoplasty — the profile line
    swift: (
      <g {...common}>
        <path d="M17 6c7 0 12 6 12 13 0 4-1 6 2 9l4 5c1 2 0 3-2 3h-4" strokeOpacity=".95" />
        <path d="M29 36c0 4-4 7-9 8" strokeOpacity=".6" />
        <path d="M13 14v26" strokeOpacity=".35" strokeDasharray="3 4" />
      </g>
    ),
    // Comprehensive — the thirds and the midline together
    leo: (
      <g {...common}>
        {head}
        <path d="M10 16h28M10 28h28" strokeDasharray="3 4" strokeOpacity=".9" />
        <path d="M24 6v36" strokeDasharray="3 4" strokeOpacity=".7" />
      </g>
    ),
  };

  const family = String(agentKey || '').split('_')[0];
  return (
    <svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true">
      {marks[family] || marks.leo}
    </svg>
  );
}

/** One masked metric row — the layout of the real report, value withheld. */
function LockedRow({ label, hint, width }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.15,
        borderBottom: '1px solid rgba(192,167,134,.16)',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '.68rem',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#C0A786',
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ mt: 0.4, fontSize: '.85rem', lineHeight: 1.65, color: '#A9B6C8' }}>
          {hint}
        </Typography>
      </Box>

      {/* the value, withheld */}
      <Box
        sx={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.6,
          borderRadius: 999,
          background: 'rgba(192,167,134,.10)',
          border: '1px solid rgba(192,167,134,.30)',
        }}
      >
        <Box
          sx={{
            width,
            height: 8,
            borderRadius: 999,
            background:
              'repeating-linear-gradient(90deg,#C0A786 0 6px,transparent 6px 11px)',
            opacity: 0.55,
          }}
        />
        <LockOutlinedIcon sx={{ fontSize: 13, color: '#C0A786', opacity: 0.9 }} />
      </Box>
    </Box>
  );
}

export default function AgentShowcase({ agent, lang, t }) {
  const all = agent.measures || [];
  // Show a few; say honestly how many more the report carries. Three rows with
  // nothing after them implies three is all there is.
  const SHOWN = 3;
  const measures = all.slice(0, SHOWN);
  const more = Math.max(0, (agent.measureCount ?? all.length) - measures.length);
  const widths = [46, 34, 40, 30];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        p: { xs: 2.5, sm: 3 },
        background:
          'radial-gradient(120% 90% at 85% 0%, rgba(192,167,134,.16), transparent 62%),' +
          'linear-gradient(165deg,#1B2C4A 0%,#11223E 55%,#0A1425 100%)',
        border: '1px solid rgba(192,167,134,.26)',
        boxShadow: '0 26px 54px -34px rgba(10,20,37,.9)',
      }}
    >
      {/* a slow sweep, as if the card itself were being read */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          insetInline: 0,
          top: 0,
          height: 120,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(232,220,200,0) 0%, rgba(232,220,200,.10) 50%, rgba(232,220,200,0) 100%)',
          animation: 'agentSweep 9s ease-in-out infinite',
          '@keyframes agentSweep': {
            '0%,100%': { transform: 'translateY(-120px)', opacity: 0 },
            '45%': { opacity: 1 },
            '90%': { transform: 'translateY(420px)', opacity: 0 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
        }}
      />

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ position: 'relative' }}>
        <Box
          sx={{
            flex: 'none',
            width: 60,
            height: 60,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 3,
            background: 'rgba(192,167,134,.08)',
            border: '1px solid rgba(192,167,134,.28)',
          }}
        >
          <Emblem agentKey={agent.key} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '.65rem',
              letterSpacing: '.28em',
              textTransform: 'uppercase',
              color: '#C0A786',
              fontWeight: 700,
            }}
          >
            {t('preview.agentEyebrow')}
          </Typography>
          <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: '1.2rem', color: '#fff' }}>
            {localized(agent.name, lang)}
          </Typography>
          {localized(agent.description, lang) && (
            <Typography sx={{ mt: 0.75, fontSize: '.9rem', lineHeight: 1.8, color: '#B3BFD0' }}>
              {localized(agent.description, lang)}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* the report's own shape, values withheld */}
      <Box
        sx={{
          position: 'relative',
          mt: 2.5,
          px: { xs: 1.75, sm: 2.25 },
          py: 0.5,
          borderRadius: 3,
          background: 'rgba(255,255,255,.035)',
          border: '1px solid rgba(192,167,134,.18)',
        }}
      >
        {measures.map((m, i) => (
          <LockedRow
            key={i}
            label={localized(m.k, lang)}
            hint={localized(m.v, lang)}
            width={widths[i % widths.length]}
          />
        ))}

        {more > 0 && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              py: 1.15, borderTop: '1px solid rgba(192,167,134,.16)',
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[0, 1, 2].map((d) => (
                <Box key={d} sx={{ width: 4, height: 4, borderRadius: '50%',
                  background: '#C0A786', opacity: 0.5 + d * 0.15 }} />
              ))}
            </Box>
            <Typography sx={{ fontSize: '.82rem', color: '#C0A786', fontWeight: 700 }}>
              {t('preview.andMore', { count: more })}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          position: 'relative',
          mt: 1.75,
          fontSize: '.8rem',
          lineHeight: 1.7,
          color: '#8FA0B8',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 15, color: '#C0A786' }} />
        {t('preview.agentLockedNote')}
      </Typography>
    </Box>
  );
}
